// @ts-nocheck
import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { STUDIO_TIPS } from "@/lib/carouselStudioMeta";
import { AiActionButton } from "@/components/studioTabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { OpRoleBadge } from "@/components/op/OpRoleBadge";
import { OP_RESULT_CARD, OP_SURFACE } from "@/lib/opSurfaces";
import ConceptsJobInline from "@/components/ConceptsJobInline";
import CzasotekstPanel from "@/components/CzasotekstPanel";
import FeedPostPanel from "@/components/FeedPostPanel";

import * as Helpers from "../helpers";
const { ideaDescription, InspirationPanel, CopyPackButton } = Helpers;

export default function CopyTab({ ctx }: { ctx: any }) {
  const {
    abortLlmM,
    aiAbort,
    aiError,
    aiFailed,
    aiJob,
    aiJobParams,
    aiStatusLabel,
    analyzeInspM,
    caption,
    clickAi,
    conceptsRunning,
    copyBusy,
    createM,
    goTab,
    hasCopySlides,
    hasSelectedIdea,
    isOperator,
    item,
    selectM,
    selectedIdea,
    setCaption,
    showPicker,
    traceOut,
    uploadInspM,
  } = ctx;

  const isCzasotekst =
    item?.kind === "czasotekst" ||
    (item as { content?: { kind?: string } } | undefined)?.content?.kind === "czasotekst";
  if (isCzasotekst && item) {
    return (
      <TabsContent value="copy" className="mt-0">
        <CzasotekstPanel
          item={item}
          caption={caption}
          section="content"
          onCaption={setCaption}
          onHook={(v) => {
            void v;
          }}
        />
      </TabsContent>
    );
  }

  const isFeedPost =
    item?.kind === "feed_post" ||
    (item as { content?: { kind?: string } } | undefined)?.content?.kind === "feed_post";
  if (isFeedPost && item) {
    const feedPostCopyM = ctx.feedPostCopyM;
    const hasHeading = Boolean(String(item.copy?.slides?.[0]?.heading || item.hook || "").trim());
    const hasCaption = Boolean(String(item.caption || caption || "").trim());
    const hasFeedText = hasHeading && hasCaption;

    if (isOperator) {
      return (
        <TabsContent value="copy" className="mt-0">
          <FeedPostPanel
            item={item}
            caption={caption}
            onCaption={setCaption}
            onGoRender={() => goTab("render")}
            autoStart={false}
            abortPending={abortLlmM.isPending}
            onAbort={aiAbort}
          />
        </TabsContent>
      );
    }

    return (
      <TabsContent value="copy" className="mt-0 space-y-5">
        <Card id="concepts" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base text-slate-900">
              Koncepcje posta {item?.short_id ? `· ${item.short_id}` : ""}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ConceptsJobInline
              item={item}
              params={typeof aiJobParams === "function" ? aiJobParams() : []}
              running={Boolean(conceptsRunning)}
              failed={Boolean(aiFailed && (aiJob === "concepts" || createM.isError))}
              statusLabel={aiJob === "concepts" || conceptsRunning ? aiStatusLabel?.() : undefined}
              error={aiJob === "concepts" || conceptsRunning || createM.isError ? aiError : undefined}
              abortPending={abortLlmM.isPending}
              onAbort={aiAbort}
            />
            {(item?.ideas || []).length > 0 && !hasSelectedIdea ? (
              <p className="text-xs text-muted-foreground">
                Klik zaznacza kartę (format Warsztatu + kąt). Potem „Generuj treść” pisze heading i caption.
              </p>
            ) : null}
            <div id="studio-concepts" className={cn("space-y-3 p-3", OP_SURFACE.result.box)}>
              <OpRoleBadge tone="result" label="Koncepcje" />
              <div className="grid gap-3 md:grid-cols-2">
                {(item?.ideas || []).map((idea) => (
                  <button
                    key={idea.id}
                    type="button"
                    disabled={selectM.isPending || copyBusy || feedPostCopyM?.isPending}
                    onClick={() => {
                      if (idea.selected) return;
                      selectM.mutate(idea.id);
                    }}
                    className={cn(
                      "p-3 text-sm transition-colors",
                      idea.selected ? OP_RESULT_CARD.selected : OP_RESULT_CARD.idle,
                    )}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <Badge variant="outline">{idea.format_id || idea.format_source}</Badge>
                      <span className="text-xs text-muted-foreground">{idea.format_note}</span>
                    </div>
                    <div className="font-medium">{idea.hook}</div>
                    {ideaDescription(idea) ? (
                      <p className="mt-2 text-xs text-muted-foreground">{ideaDescription(idea)}</p>
                    ) : null}
                    {idea.effect ? (
                      <p className="mt-2 text-xs">
                        <span className="font-medium">Efekt: </span>
                        {idea.effect}
                      </p>
                    ) : null}
                    {idea.tool_line ? (
                      <p className="mt-1 text-xs text-muted-foreground">Caption: {idea.tool_line}</p>
                    ) : null}
                  </button>
                ))}
                {!conceptsRunning && (item?.ideas || []).length === 0 ? (
                  <p className="col-span-full text-sm text-muted-foreground">
                    Tu pojawią się 4 karty po starcie z briefu (źródła → Flash).
                  </p>
                ) : null}
              </div>
            </div>

            <div id="studio-primary-cta" className="flex flex-wrap items-start gap-3">
              <AiActionButton
                className="w-auto"
                label={hasFeedText ? "Przegeneruj treść" : "Generuj treść"}
                io="post"
                variant="brand"
                running={Boolean(feedPostCopyM?.isPending || item.llm_status === "feed_post_copy")}
                failed={Boolean(feedPostCopyM?.isError || item.llm_status === "error")}
                disabled={!item || !hasSelectedIdea || conceptsRunning || item.llm_status === "feed_post_copy"}
                onClick={() => feedPostCopyM?.mutate(item.id)}
                icon={<MessageSquare className="mr-2 h-3.5 w-3.5" />}
              />
              {hasSelectedIdea && selectedIdea?.hook ? (
                <p className="w-full text-xs text-muted-foreground">
                  Wybrano: {selectedIdea.hook}
                  {selectedIdea.format_id ? ` · ${selectedIdea.format_id}` : ""}
                </p>
              ) : (
                <p className="w-full text-xs text-muted-foreground">Najpierw wybierz jedną z 4 kart.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {hasSelectedIdea || hasFeedText ? (
          <FeedPostPanel
            item={item}
            caption={caption}
            onCaption={setCaption}
            onGoRender={() => goTab("render")}
            autoStart={false}
            abortPending={abortLlmM.isPending}
            onAbort={aiAbort}
          />
        ) : null}
      </TabsContent>
    );
  }

  return (
    <TabsContent value="copy" className="mt-0 space-y-5">
      {showPicker ? (
        <Card id="concepts" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base text-slate-900">Koncepcje</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Najpierw wybierz przebieg w briefie.</p>
          </CardContent>
        </Card>
      ) : isOperator ? (
        <Card id="concepts" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base text-slate-900">Koncepcje</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Pominięte. Tor własny tekst nie składa 4 kart.</p>
            {item ? (
              <InspirationPanel
                item={item}
                uploadM={uploadInspM}
                analyzeM={analyzeInspM}
                track="operator"
                onAnalyze={(opts) => clickAi("inspiration", () => analyzeInspM.mutate(opts))}
              />
            ) : null}
            <Button type="button" variant="brand" onClick={() => goTab("generator")}>
              Idź do Generatora treści
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card id="concepts" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-base text-slate-900">
              <span>Koncepcje {item?.short_id ? `· ${item.short_id}` : ""}</span>
              {item?.latest_generation?.pack || (item?.ideas?.length || 0) >= 4 ? (
                <CopyPackButton item={item} outgoing={traceOut} />
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ConceptsJobInline
              item={item}
              params={typeof aiJobParams === "function" ? aiJobParams() : []}
              running={Boolean(conceptsRunning)}
              failed={Boolean(aiFailed && (aiJob === "concepts" || createM.isError))}
              statusLabel={aiJob === "concepts" || conceptsRunning ? aiStatusLabel?.() : undefined}
              error={aiJob === "concepts" || conceptsRunning || createM.isError ? aiError : undefined}
              abortPending={abortLlmM.isPending}
              onAbort={aiAbort}
            />
            {!item && !conceptsRunning && (
              <p className="text-sm text-muted-foreground">Wybierz brief albo karuzelę z kolejki.</p>
            )}
            {item ? (
              <InspirationPanel
                item={item}
                uploadM={uploadInspM}
                analyzeM={analyzeInspM}
                track="ai"
                onAnalyze={(opts) => clickAi("inspiration", () => analyzeInspM.mutate(opts))}
              />
            ) : null}
            {(item?.ideas || []).length > 0 && (
              <p className="text-xs text-muted-foreground">
                Klik zaznacza kartę. Typ i styl ustawiasz w Generatorze treści. Generacja startuje dopiero po „Generuj
                treść”.
              </p>
            )}
            <div id="studio-concepts" className={cn("space-y-3 p-3", OP_SURFACE.result.box)}>
              <OpRoleBadge tone="result" label="Koncepcje" />
              <div className="grid gap-3 md:grid-cols-2">
                {(item?.ideas || []).map((idea) => (
                  <button
                    key={idea.id}
                    type="button"
                    disabled={selectM.isPending || copyBusy}
                    onClick={() => {
                      if (idea.selected) return;
                      selectM.mutate(idea.id);
                    }}
                    className={cn(
                      "p-3 text-sm transition-colors",
                      idea.selected ? OP_RESULT_CARD.selected : OP_RESULT_CARD.idle,
                    )}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <Badge variant="outline">{idea.format_id || idea.format_source}</Badge>
                      <span className="text-xs text-muted-foreground">{idea.format_note}</span>
                    </div>
                    <div className="font-medium">{idea.hook}</div>
                    {ideaDescription(idea) ? (
                      <p className="mt-2 text-xs text-muted-foreground">{ideaDescription(idea)}</p>
                    ) : null}
                    {idea.effect ? (
                      <p className="mt-2 text-xs">
                        <span className="font-medium">Efekt: </span>
                        {idea.effect}
                      </p>
                    ) : null}
                  </button>
                ))}
                {!conceptsRunning && (item?.ideas || []).length === 0 ? (
                  <p className="col-span-full text-sm text-muted-foreground">
                    Tu pojawią się 4 karty po starcie z briefu.
                  </p>
                ) : null}
              </div>
            </div>

            <div id="studio-primary-cta" className="flex flex-wrap items-start gap-3">
              <AiActionButton
                className="w-auto"
                label={hasCopySlides ? "Wróć do Generatora treści" : "Opracuj treść"}
                io="copy"
                variant="brand"
                running={false}
                failed={false}
                disabled={!item || !hasSelectedIdea}
                onClick={() => goTab("generator")}
                icon={<MessageSquare className="mr-2 h-3.5 w-3.5" />}
              />
              {hasSelectedIdea && selectedIdea?.hook ? (
                <p className="w-full text-xs text-muted-foreground">
                  Wybrana karta: {selectedIdea.hook}. {STUDIO_TIPS.generateCopy}
                </p>
              ) : (
                <p className="w-full text-xs text-muted-foreground">
                  Wybierz kartę, potem Opracuj treść. Generator nie startuje sam.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </TabsContent>
  );
}
